import { useState, useEffect, useEffectEvent, type ReactNode } from 'react'
import { SlidersHorizontal, Check, ChevronDown, Minus, Plus } from 'lucide-react'
import type { AnchorVisibleWindow } from '@edge/charts'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useChartInteraction } from '@/context/chart-interaction-hooks'
import { useLayoutPanel } from '@/workspace/use-layout-panel'
import {
  WINDOW_PRESETS,
  WINDOW_ANCHOR_POINTS,
  MAX_FORWARD_SESSIONS,
  matchPresetId,
  resolveWindow,
  clampWindowToBudget,
  anchorLabelForMin,
  dayLabelForOffset,
  boundRank,
  minToLabel,
  maxSpanSessions,
  type SessionPoint,
} from '@/features/history/components/history-chart-grid-window-presets'

interface MiniOption {
  key: string
  label: ReactNode
  selected: boolean
  disabled?: boolean
  onSelect: () => void
}

/**
 * Small header-style dropdown (Popover, never `Select`/`DropdownMenu`) for the
 * Point column of a Custom bound. The check slot is always reserved so
 * selecting never shifts the row.
 */
function MiniSelect({
  triggerTestId,
  optionTestIdPrefix,
  triggerLabel,
  options,
}: {
  triggerTestId: string
  optionTestIdPrefix: string
  triggerLabel: ReactNode
  options: MiniOption[]
}) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={triggerTestId}
          className="flex h-7 flex-1 items-center justify-between gap-1 rounded border border-input bg-background/55 px-2 text-sm text-foreground transition-colors hover:bg-hover"
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronDown size={13} strokeWidth={1.8} className="shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-36 p-1"
        align="start"
        sideOffset={4}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {options.map((o) => (
          <button
            key={o.key}
            type="button"
            disabled={o.disabled}
            data-testid={`${optionTestIdPrefix}-${o.key}`}
            aria-pressed={o.selected}
            onClick={() => {
              o.onSelect()
              setOpen(false)
            }}
            className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm text-foreground hover:bg-hover disabled:pointer-events-none disabled:opacity-30"
          >
            <span>{o.label}</span>
            <Check
              size={13}
              strokeWidth={2}
              className={`text-accent-foreground ${o.selected ? '' : 'invisible'}`}
            />
          </button>
        ))}
      </PopoverContent>
    </Popover>
  )
}

/**
 * Day picker for one bound: a ± stepper over the session offset, shown in
 * History's D[n] vocabulary (D[-2] … D[1] … D[2]). Steps back for prior context,
 * forward for the outcome day. Enablement is caller-driven (span budget +
 * forward cap + no-invert), so the arrows disable at the real limits.
 */
function DayOffsetStepper({
  testId,
  offset,
  canDecrement,
  canIncrement,
  onChange,
}: {
  testId: string
  offset: number
  canDecrement: boolean
  canIncrement: boolean
  onChange: (next: number) => void
}) {
  const btn =
    'flex h-full w-6 items-center justify-center text-muted-foreground transition-colors ' +
    'enabled:hover:text-foreground disabled:opacity-30'
  return (
    <div className="flex h-7 items-center rounded border border-input bg-background/55">
      <button
        type="button"
        data-testid={`${testId}-dec`}
        disabled={!canDecrement}
        onClick={() => onChange(offset - 1)}
        className={btn}
        aria-label="Earlier day"
      >
        <Minus size={12} strokeWidth={2} />
      </button>
      <span
        data-testid={`${testId}-value`}
        className="w-12 text-center text-sm tabular-nums text-foreground"
      >
        {dayLabelForOffset(offset)}
      </span>
      <button
        type="button"
        data-testid={`${testId}-inc`}
        disabled={!canIncrement}
        onClick={() => onChange(offset + 1)}
        className={btn}
        aria-label="Later day"
      >
        <Plus size={12} strokeWidth={2} />
      </button>
    </div>
  )
}

/**
 * Presentational visible-window gear, shared by the History Chart Grid grid
 * and regular charts in history mode. Pure/controlled: it renders `window` and
 * emits every change via `onChange` — the caller owns where the window persists
 * (grid panel state for History; global ChartInteractionSettings for regular
 * charts). Presets cover the common single-day windows; Custom builds a window
 * from a Day (D[n]) + a Point per bound. `testIdPrefix` keys every element so
 * each mount site keeps its own stable test ids.
 *
 * See docs/superpowers/specs/2026-08-12-history-chart-window-anchors-*.
 */
export function ChartWindowControl({
  window: win,
  periodMs,
  onChange,
  testIdPrefix,
  title = 'Visible hours',
}: {
  /** Current (already-resolved) window — never undefined; caller defaults it. */
  window: AnchorVisibleWindow
  /** Timeframe in ms — bounds how far the Day steppers may reach (bar budget). */
  periodMs: number
  /** Emits the next window. Presets emit `{ startMin, endMin }` (no offsets);
   *  Custom emits offsets too. */
  onChange: (next: AnchorVisibleWindow) => void
  testIdPrefix: string
  title?: string
}) {
  const [open, setOpen] = useState(false)
  // "Custom" is a sticky selection, not derived: a custom window can coincide
  // with a preset's minutes, and the user may open the anchors while a preset is
  // active. Cleared whenever a preset row is chosen.
  const [customMode, setCustomMode] = useState(false)

  const maxSpan = maxSpanSessions(periodMs)
  const startOff = win.startDayOffset ?? 0
  const endOff = win.endDayOffset ?? 0
  const activeId = customMode ? 'custom' : matchPresetId(win)

  // Valid ⟺ Start strictly before End, the span fits the bar budget, and the
  // forward reach is within the loadable outcome window.
  const isValid = (w: AnchorVisibleWindow): boolean => {
    const s = w.startDayOffset ?? 0
    const e = w.endDayOffset ?? 0
    if (boundRank(s, w.startMin) >= boundRank(e, w.endMin)) return false
    if (e - s + 1 > maxSpan) return false
    if (e > MAX_FORWARD_SESSIONS) return false
    return true
  }

  const mk = (startMin: number, endMin: number, s: number, e: number): AnchorVisibleWindow => {
    const next: AnchorVisibleWindow = { startMin, endMin }
    if (s !== 0) next.startDayOffset = s
    if (e !== 0) next.endDayOffset = e
    return next
  }

  const setWindow = (next: AnchorVisibleWindow) => {
    setCustomMode(true)
    onChange(next)
  }

  const selectPreset = (next: AnchorVisibleWindow) => {
    setCustomMode(false)
    onChange({ startMin: next.startMin, endMin: next.endMin })
  }

  const setStartOffset = (s: number) => setWindow(mk(win.startMin, win.endMin, s, endOff))
  const setEndOffset = (e: number) => setWindow(mk(win.startMin, win.endMin, startOff, e))
  const setStartPoint = (p: SessionPoint) => setWindow(mk(p.min, win.endMin, startOff, endOff))
  const setEndPoint = (p: SessionPoint) => setWindow(mk(win.startMin, p.min, startOff, endOff))

  // Re-clamp both bounds when the timeframe's cap shrinks (e.g. a D[-30] window
  // switching from 1h to 1m). clampWindowToBudget returns the SAME object when
  // already within budget, so this only fires an onChange on a real change.
  const reclamp = useEffectEvent((cap: number) => {
    const clamped = clampWindowToBudget(win, cap)
    if (clamped && clamped !== win) onChange(clamped)
  })
  useEffect(() => {
    reclamp(periodMs)
  }, [periodMs])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={title}
          aria-label={title}
          data-testid={`${testIdPrefix}-trigger`}
          onMouseDown={(e) => e.stopPropagation()}
          onDragStart={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          draggable
          className="flex h-6 w-6 items-center justify-center rounded border-none bg-transparent text-muted-foreground transition-colors hover:text-foreground"
          style={{ cursor: 'pointer' }}
        >
          <SlidersHorizontal size={14} strokeWidth={1.8} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-1"
        align="end"
        sideOffset={4}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </div>
        {WINDOW_PRESETS.map((preset) => {
          const isActive = activeId === preset.id
          return (
            <button
              key={preset.id}
              type="button"
              data-testid={`${testIdPrefix}-preset-${preset.id}`}
              aria-pressed={isActive}
              onClick={() => selectPreset(preset.window)}
              className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm text-foreground hover:bg-hover"
            >
              <span>{preset.label}</span>
              <span className="flex items-center gap-1.5">
                <span className="text-[11px] tabular-nums text-muted-foreground">
                  {minToLabel(preset.window.startMin)} – {minToLabel(preset.window.endMin)}
                </span>
                <Check
                  size={14}
                  strokeWidth={2}
                  className={`text-accent-foreground ${isActive ? '' : 'invisible'}`}
                />
              </span>
            </button>
          )
        })}

        {/* Custom is a row like the presets; selecting it reveals the anchors. */}
        <button
          type="button"
          data-testid={`${testIdPrefix}-custom`}
          aria-pressed={activeId === 'custom'}
          onClick={() => setCustomMode(true)}
          className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-sm text-foreground hover:bg-hover"
        >
          <span>Custom</span>
          <Check
            size={14}
            strokeWidth={2}
            className={`text-accent-foreground ${activeId === 'custom' ? '' : 'invisible'}`}
          />
        </button>

        {activeId === 'custom' && (
          <div className="pt-0.5">
            {/* Start: which day (back for context) + a session point. */}
            <div className="flex items-center gap-1.5 px-2 py-1">
              <span className="w-9 shrink-0 text-xs text-muted-foreground">Start</span>
              <DayOffsetStepper
                testId={`${testIdPrefix}-start-day`}
                offset={startOff}
                canDecrement={isValid(mk(win.startMin, win.endMin, startOff - 1, endOff))}
                canIncrement={isValid(mk(win.startMin, win.endMin, startOff + 1, endOff))}
                onChange={setStartOffset}
              />
              <MiniSelect
                triggerTestId={`${testIdPrefix}-start`}
                optionTestIdPrefix={`${testIdPrefix}-start`}
                triggerLabel={anchorLabelForMin(win.startMin)}
                options={WINDOW_ANCHOR_POINTS.map((p) => ({
                  key: p.id,
                  label: p.label,
                  selected: p.min === win.startMin,
                  disabled: !isValid(mk(p.min, win.endMin, startOff, endOff)),
                  onSelect: () => setStartPoint(p),
                }))}
              />
            </div>
            {/* End: which day (forward for the outcome) + a session point. */}
            <div className="flex items-center gap-1.5 px-2 py-1">
              <span className="w-9 shrink-0 text-xs text-muted-foreground">End</span>
              <DayOffsetStepper
                testId={`${testIdPrefix}-end-day`}
                offset={endOff}
                canDecrement={isValid(mk(win.startMin, win.endMin, startOff, endOff - 1))}
                canIncrement={isValid(mk(win.startMin, win.endMin, startOff, endOff + 1))}
                onChange={setEndOffset}
              />
              <MiniSelect
                triggerTestId={`${testIdPrefix}-end`}
                optionTestIdPrefix={`${testIdPrefix}-end`}
                triggerLabel={anchorLabelForMin(win.endMin)}
                options={WINDOW_ANCHOR_POINTS.map((p) => ({
                  key: p.id,
                  label: p.label,
                  selected: p.min === win.endMin,
                  disabled: !isValid(mk(win.startMin, p.min, startOff, endOff)),
                  onSelect: () => setEndPoint(p),
                }))}
              />
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

/**
 * Regular-chart binding of {@link ChartWindowControl}: reads/writes the GLOBAL
 * "history time range" (`ChartInteractionSettings.historyVisibleWindow`), so the
 * window a user picks is remembered and auto-applied to EVERY chart the moment
 * it enters history mode. Absent ⇒ full session. Mount only when the chart is in
 * history mode (see PanelHeader) — the window is meaningless on a live chart.
 */
export function ChartHistoryWindowControl({ panelId }: { panelId: string }) {
  const { interaction, update } = useChartInteraction()
  const { panelState } = useLayoutPanel<{ period?: { ms: number } }>(panelId)
  const window = resolveWindow(interaction.historyVisibleWindow)
  // 1D default matches the regular chart's own default period; the clamp is a
  // no-op for the default single-day window regardless.
  const periodMs = panelState?.period?.ms ?? 86_400_000
  return (
    <ChartWindowControl
      window={window}
      periodMs={periodMs}
      onChange={(next) => update({ historyVisibleWindow: next })}
      testIdPrefix="chart-history-window"
    />
  )
}
